import { css, Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { MixinFocusVisible } from '../structure/DefaultStyles';
import { MixinTruncate, TextSize100, TextSize85 } from '../text/Typography';

export const MENU_ICON_SIZE = 16;

// A mixin rather than a styled component, so MUI's Paper never receives a stray prop.
export const MixinPopoverSurface = (theme: Theme) => css`
    background-color: ${theme.color.accentCard};
    color: ${theme.color.text};
    border: 1px solid ${theme.color.border};
    overflow: hidden;
`;

export const MixinMenuBaseLink = css`
    text-decoration: none;

    ${MixinFocusVisible}
    &:hover,
    &:focus {
        text-decoration: underline;
    }
`;

export const MixinMenuFlexContainer = css`
    display: flex;
    align-items: center;
`;

// Visually removes secondary menu content (labels, expand affordances, submenus)
// while leaving the icon rail in place.
export const MixinMenuHideContent = css`
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 0px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 0px;
    padding: 0;
    margin: 0;
`;

export const MixinMenuMediaHide = css`
    @media (max-width: 500px) {
        ${MixinMenuHideContent}
    }
`;

// Collapses menu text/secondary content to an icon-only rail when an ancestor
// is marked with data-collapsed="true" (driven by the sidebar collapse toggle).
export const MixinMenuCollapseHide = css`
    [data-collapsed='true'] & {
        ${MixinMenuHideContent}
    }
`;

export const MenuActiveBar = styled.span<{ isActive: boolean; isSubActive: boolean }>`
    flex-shrink: 0;
    width: 6px;
    min-width: 6px;
    max-width: 6px;
    height: 1.75rem;
    margin: 0.25rem 0;
    background-color: ${(props) => (props.isActive ? props.theme.color.accent : 'transparent')};
`;

// MenuTruncate folds away with the icon rail; use this where the label must stay visible.
export const MenuLabelText = styled.span<{ $isActive?: boolean }>`
    ${TextSize85}
    ${MixinTruncate}
    padding: 0 0.25rem 0 0;
`;

export const MenuTruncate = styled(MenuLabelText)`
    ${MixinMenuMediaHide}
    ${MixinMenuCollapseHide}
`;

export const MixinMenuItemRow = (theme: Theme) => css`
    display: flex;
    align-items: center;
    width: 100%;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    ${TextSize100}
    color: ${theme.color.text};
    &:hover,
    &:focus {
        background-color: ${theme.color.border};
        outline: none;
    }

    ${MixinFocusVisible}
`;

export const MixinMenuItemLabel = (theme: Theme) => css`
    color: ${theme.color.textBody};
    &.active,
    &:hover,
    &:focus {
        color: ${theme.color.text};
    }
`;

export const MixinMenuSubItemRow = (theme: Theme) => css`
    ${MixinMenuFlexContainer}
    width: 100%;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    &:hover,
    &:focus {
        background-color: ${theme.color.border};
        outline: none;
    }

    ${MixinFocusVisible}
`;

export const MixinMenuSubItemLabel = (theme: Theme) => css`
    ${TextSize85}
    ${MixinMenuItemLabel(theme)}
`;
